import { useEffect } from "react";
import { useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import type { ProjectMembersStoreInstance } from "@/stores/pm/ProjectMembersStore";
import type { TemplateFormMembers } from "../components/templateForm/PersonRowsField";

/** Collapses the store's three indistinguishable empty reads into a state
 *  the member picker can be honest about. "Not cached and not loading" means
 *  the open-form fetch already ran and failed — the store leaves the cache
 *  miss in place exactly so a retry can refetch. No project means there is no
 *  list to fetch at all — `ready`-and-empty here would read as "this project
 *  has no members". */
function memberStatus(
  store: ProjectMembersStoreInstance,
  projectId: number | null
): TemplateFormMembers["status"] {
  if (projectId === null) return "noProject";
  if (store.has(projectId)) return "ready";
  if (store.isLoading(projectId)) return "loading";
  return "error";
}

/**
 * The project's members for the person rows, fetched only once a form is
 * open — that is the first moment they are needed, so the RPC is paid then.
 */
export function useTemplateFormMembers(
  projectId: string | null,
  active: boolean
): TemplateFormMembers {
  const store = useProjectMembersStore();
  const id = projectId ? Number(projectId) : null;

  useEffect(() => {
    if (active && id !== null) store.fetch(id);
  }, [active, id, store]);

  return {
    list: id !== null ? store.get(id) : [],
    status: memberStatus(store, id),
    retry: () => {
      if (id !== null) store.fetch(id);
    },
  };
}
